import { useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Link } from "react-router-dom";
import { Star, Store, ShoppingBag, ArrowRight } from "lucide-react";

const STATE_CENTERS = {
  TX: [29.7604, -95.3698],   // Houston
  CO: [39.7392, -104.9903],  // Denver
  CA: [34.0522, -118.2437],  // LA
  NV: [36.1699, -115.1398],  // Vegas
  ALL: [35, -100],
};

// Custom marker icon factory (sticker style)
const makeIcon = (active = false, isMedical = false, isHemp = false) => {
  const color = active ? "#15FF73" : isMedical ? "#00E0F5" : isHemp ? "#FFE74C" : "#FF1A8C";
  const txt = active ? "#0B0F0C" : "#0B0F0C";
  return L.divIcon({
    className: "",
    html: `<div style="position:relative;transform:translate(-50%,-100%);">
      <div style="
        background:${color};
        color:${txt};
        border:2.5px solid #0B0F0C;
        box-shadow:3px 3px 0 #0B0F0C;
        border-radius:50%;
        width:38px;height:38px;
        display:flex;align-items:center;justify-content:center;
        font-weight:800;font-family:'Bricolage Grotesque',system-ui;
        transform:rotate(${active ? "-4deg" : "0deg"});
        transition: transform 200ms;
      ">📍</div>
      <div style="
        position:absolute;left:50%;bottom:-6px;transform:translateX(-50%);
        width:0;height:0;
        border-left:7px solid transparent;border-right:7px solid transparent;
        border-top:9px solid #0B0F0C;
      "></div>
    </div>`,
    iconSize: [38, 50],
    iconAnchor: [19, 50],
    popupAnchor: [0, -50],
  });
};

function FlyTo({ center, zoom = 11 }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo(center, zoom, { duration: 0.8 });
  }, [center, zoom, map]);
  return null;
}

export default function InteractiveMap({
  shops = [],
  stateCode = "CO",
  activeId = null,
  onPick,
  onPreorder,
  height = 480,
}) {
  const center = activeId
    ? (() => { const s = shops.find((x) => x.id === activeId); return s && s.lat ? [s.lat, s.lng] : STATE_CENTERS[stateCode] || STATE_CENTERS.ALL; })()
    : STATE_CENTERS[stateCode] || STATE_CENTERS.ALL;
  const tileToken = process.env.REACT_APP_MAPBOX_TOKEN;
  const tileUrl = tileToken
    ? `https://api.mapbox.com/styles/v1/mapbox/dark-v11/tiles/{z}/{x}/{y}?access_token=${tileToken}`
    : "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png"; // free dark tiles
  const tileAttribution = tileToken
    ? '© <a href="https://www.mapbox.com/">Mapbox</a> © <a href="https://www.openstreetmap.org/copyright">OSM</a>'
    : '© <a href="https://www.openstreetmap.org/copyright">OSM</a> © <a href="https://carto.com/attributions">CARTO</a>';

  return (
    <div className="rounded-3xl overflow-hidden border-2 border-[#0B0F0C] shadow-[6px_6px_0_#15FF73] relative" style={{ height }} data-testid="interactive-map">
      <MapContainer
        center={center}
        zoom={11}
        scrollWheelZoom
        style={{ height: "100%", width: "100%", background: "#0B0F0C" }}
      >
        <TileLayer url={tileUrl} attribution={tileAttribution} />
        <FlyTo center={center} zoom={activeId ? 13 : 11} />
        {shops.filter((s) => s.lat && s.lng).map((s) => (
          <Marker
            key={s.id}
            position={[s.lat, s.lng]}
            icon={makeIcon(activeId === s.id, s.type === "medical", s.type === "hemp")}
            eventHandlers={{ click: () => onPick && onPick(s) }}
          >
            <Popup className="xseed-popup" closeButton={false}>
              <div style={{ minWidth: 240 }} className="font-body">
                <div className="flex gap-3 items-start mb-2">
                  <img src={s.image_url} alt="" className="w-14 h-14 object-cover rounded-xl border-2 border-[#0B0F0C]" />
                  <div className="flex-1">
                    <div className="font-display font-extrabold text-lg leading-tight">{s.name}</div>
                    <div className="text-[10px] font-mono-x font-bold uppercase tracking-widest text-[#0B0F0C]/55">{s.city}, {s.state} · {s.type}</div>
                    <div className="flex items-center gap-1 text-xs mt-1"><Star size={11} className="fill-[#FF7A3D] text-[#FF7A3D]" /> <b>{(s.rating || 4.5).toFixed(1)}</b></div>
                  </div>
                </div>
                <p className="text-xs text-[#0B0F0C]/70 leading-relaxed mb-3">{s.description}</p>
                <div className="grid grid-cols-2 gap-2">
                  <Link to={`/dispensary/${s.id}`} className="btn-x btn-ghost !py-1.5 !px-3 !text-[11px]"><Store size={11} strokeWidth={2.4} /> Visit</Link>
                  <button onClick={() => onPreorder && onPreorder(s)} className="btn-x btn-primary !py-1.5 !px-3 !text-[11px]"><ShoppingBag size={11} strokeWidth={2.4} /> Pre-order</button>
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
      <div className="absolute top-3 left-3 z-[400] sticker sticker-green !text-[10px] pointer-events-none">
        <span className="pulse-dot mr-1" /> Live · {stateCode}
      </div>
      <div className="absolute top-3 right-3 z-[400] sticker !text-[10px] pointer-events-none">{shops.filter((s) => s.lat).length} shops</div>
    </div>
  );
}
